import {
  selectLoading,
  selectPermissionsError,
  selectPermissionsInitialized,
} from 'store/features/permissions/selector';
import { FormattedMessage } from 'react-intl';
import { useMeetingBlurListener } from 'hooks';
import { useLocation } from 'react-router-dom';
import ChatIcon from '@mui/icons-material/Chat';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Authentication, isStandalonePublicRoute } from 'utils';
import permissionsActions from 'store/features/permissions/actions';
import { BlurOverlay, Header, SideBar, ChatDrawer } from 'components';
import { Box, Toolbar, Fab, Zoom, Tooltip, keyframes } from '@mui/material';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

const PERMISSIONS_RETRY_DELAYS_MS = [1000, 2000] as const;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.7);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(79, 70, 229, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
  }
`;

type LayoutProps = {
  children: ReactElement;
};

const Layout = ({ children }: LayoutProps) => {
  // other hooks
  useMeetingBlurListener();
  const router = useLocation();
  const permissionsRetryCountRef = useRef(0);

  // useState hooks
  const [chatOpen, setChatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // sync helper functions
  const handleMobileMenuToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // redux hooks
  const dispatch = useAppDispatch();
  const permissionsLoading = useAppSelector(selectLoading);
  const permissionsError = useAppSelector(selectPermissionsError);
  const didInitFetchPermissions = useAppSelector(selectPermissionsInitialized);

  // dynamic variables
  const hasSession = Authentication.hasSession();
  const isStandalonePublicPage = isStandalonePublicRoute(router.pathname);

  // useEffect hooks
  useEffect(() => {
    if (!hasSession || isStandalonePublicPage || didInitFetchPermissions) {
      permissionsRetryCountRef.current = 0;
      return;
    }

    if (permissionsLoading) return;

    if (!permissionsError) {
      dispatch(permissionsActions.initialize());
      return;
    }

    const retryDelay = PERMISSIONS_RETRY_DELAYS_MS[permissionsRetryCountRef.current];
    if (retryDelay === undefined) return;

    const retryTimeout = window.setTimeout(() => {
      permissionsRetryCountRef.current += 1;
      dispatch(permissionsActions.initialize());
    }, retryDelay);

    return () => window.clearTimeout(retryTimeout);
  }, [didInitFetchPermissions, hasSession, isStandalonePublicPage, permissionsError, permissionsLoading, dispatch]);

  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  if (!hasSession || isStandalonePublicPage) return children;

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onMobileMenuToggle={handleMobileMenuToggle} />
      <BlurOverlay />
      <SideBar mobileOpen={mobileOpen} onMobileClose={handleMobileMenuClose} />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
        <Toolbar />
        {children}
      </Box>

      {/* Floating Action Button for Chat */}
      <Zoom in={hasSession}>
        <Tooltip title={<FormattedMessage id="chat.openAssistant" defaultMessage="AI Asistana Sor" />} placement="left">
          <Fab
            color="primary"
            aria-label="chat"
            onClick={() => setChatOpen((prev) => !prev)}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 20px rgba(79, 70, 229, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
              animation: `${pulse} 2s infinite`,
            }}>
            <ChatIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
    </Box>
  );
};

export default Layout;
